
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOMetadata from '@/components/SEOMetadata';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const GeneralTOS = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOMetadata
        title="Terms of Service | General Terms | NEXX TELECOM"
        description="General Terms of Service for NEXX TELECOM services including acceptable use policy, account responsibilities, and service agreements."
        keywords="terms of service, TOS, NEXX terms, service agreement, acceptable use policy"
        canonical="https://nexx.net/general-tos"
        robots="noindex, nofollow"
      />
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <div className="mb-8">
            <Link to="/">
              <Button variant="ghost" className="text-nexx-green hover:text-green-700">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center">
              <span className="mr-3">📜</span>
              NEXX TELECOM – Terms of Service (General)
            </h1>
            <p className="text-lg text-gray-600">
              Effective Date: June 4th, 2025
            </p>
            <div className="w-24 h-px bg-gray-300 mx-auto mt-6"></div>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-8">
              Welcome to NEXX TELECOM. By accessing or using our website and services, you agree to the following terms. These terms govern your use of all services provided by NEXX TELECOM LLC through our platform, including but not limited to VPN access, proxy services, server rentals, and network infrastructure consulting.
            </p>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Acceptable Use Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">1. Acceptable Use</h2>
              <p className="text-gray-700 mb-4">You agree not to use our services for:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Any unlawful purpose (including copyright infringement, hacking, spamming, or fraud)</li>
                <li>Activities that violate U.S. or international laws or sanctions</li>
                <li>Hosting or distributing malware, phishing sites, or botnets</li>
                <li>Mining cryptocurrency or launching DDoS attacks</li>
              </ul>
              <p className="text-gray-700 mt-4">
                We reserve the right to suspend or terminate service at our sole discretion if we detect any behavior that threatens network integrity, legal compliance, or customer safety.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Account Responsibilities Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">2. Account Responsibilities</h2>
              <p className="text-gray-700">
                You are responsible for safeguarding your account credentials and ensuring that your use complies with our Acceptable Use Policy. Shared credentials or unauthorized third-party use of your account may lead to immediate suspension.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Service-Specific Terms Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">3. Service-Specific Terms</h2>
              <p className="text-gray-700 mb-4">
                Certain services are subject to their own terms, which form part of this agreement when used:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>
                  <Link 
                    to="/proxy-tos" 
                    state={{ from: 'general-tos' }} 
                    className="text-nexx-green hover:underline"
                  >
                    Proxy Services are governed by the Proxy Terms of Service
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/server-tos" 
                    state={{ from: 'general-tos' }} 
                    className="text-nexx-green hover:underline"
                  >
                    Bare Metal & VPS Services are governed by the Server Terms of Service
                  </Link>
                </li>
              </ul>
              <p className="text-gray-700 mt-4">
                Please review those documents carefully before provisioning services.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Service Availability Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">4. Service Availability</h2>
              <p className="text-gray-700">
                We strive to provide high uptime but do not guarantee uninterrupted service. Scheduled maintenance and unforeseen outages may occur. Clients are encouraged to monitor network status through our support portal.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Privacy Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">5. Privacy</h2>
              <p className="text-gray-700">
                We do not monitor traffic unless required by law. For details on data handling and storage, please refer to our Privacy Policy.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Payment & Refunds Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">6. Payment & Refunds</h2>
              <p className="text-gray-700">
                All sales are final unless otherwise specified. Certain service categories may have refundable windows based on service uptime guarantees or delivery failures — consult service-specific ToS for those terms.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Limitation of Liability Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">7. Limitation of Liability</h2>
              <p className="text-gray-700">
                NEXX TELECOM will not be held liable for indirect or consequential losses, data loss, or revenue loss arising from service interruptions or client misuse.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Changes to Agreement Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">8. Changes to This Agreement</h2>
              <p className="text-gray-700">
                We may update these terms occasionally. Continued use of our services after updates constitutes acceptance of the new terms.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Contact Section */}
            <div className="mb-12 text-center">
              <p className="text-gray-700">
                If you have any questions about these terms, please contact us at{' '}
                <a href="mailto:legal@nexx.net" className="text-nexx-green hover:underline">
                  legal@nexx.net
                </a>
              </p>
              <div className="mt-6">
                <Button className="bg-nexx-green hover:bg-green-700 text-white">
                  Contact Support
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default GeneralTOS;
